import React, { useEffect } from "react";
import { message, Typography, Tooltip, Pagination, FloatButton } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import {
  useInputContactDetails,
  getContactList,
} from "redux/slice/faxSlice";
import { useAppDispatch } from "redux/hooks";
import Table from "components/ui-component/Table";
import EditContactModal from "components/Modal/EditContactModal";
import { ConfirmationModal } from "components/ui-component/modals";
import { Button } from "components/ui-component";

import { baseUrl } from "API/baseURL";
import { CONTACTS } from "API/constAPI";

import EditDropIcon from "components/Icons/EditDropIcon";
import DeleteThinIcon from "components/Icons/DeleteThinIcon";
import SearchIcon from "components/Icons/SearchIcon";
import AddIconCollapseIcon from "components/Icons/AddIconCollapse";

import {
  FaxPageHeader,
  FaxPageStyle,
  HeaderActions,
  Search,
} from "components/fax/SecureFax.style";
import ContactsTableCard from "components/ContactsTableCard/ContactsTableCard";
import SinglePlusIcon from "components/Icons/SinglePlusIcon";
import { onGetUserInfo } from "components/Helper/onGetUserInfo";

interface ContactFilterType {
  page: number;
  per_page: number;
  name_query: string;
  order_by: string;
}

export interface ContactListType {
  contact_id: string;
  firstname: string;
  lastname: string;
  fax_number: string;
  notes: string;
  contact_source: string;
}

interface SentContactsProps {
  contactfilter: ContactFilterType;
  setContactFilter: React.Dispatch<React.SetStateAction<ContactFilterType>>;
}

const Contacts = (props: SentContactsProps) => {
  const { setContactFilter, contactfilter } = props;

  const { user } = useAuth0();
  const navigation = useNavigate();
  const ContactList = useInputContactDetails();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isEdit, setIsEdit] = React.useState<boolean>(false);
  const [data, setData] = React.useState<any>(null);

  const dispatch = useAppDispatch();
  const [deleteContact, setDeleteContact] = React.useState<string | null>(null);

  useEffect(() => {
    onContactListGet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onContactListGet = useDebouncedCallback(async () => {
    onGetUserInfo(dispatch, user, navigation);
    setIsLoading(true);
    try {
      const params = {
        ...contactfilter,
      };
      await dispatch(getContactList(params)).unwrap();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("contact list get error -->", error?.message);
    }
  }, 500);

  const onDeleteContact = async (id: any) => {
    onGetUserInfo(dispatch, user, navigation);
    const data = [id];
    const username = user?.email;
    const accessToken = localStorage.getItem("accessToken");
    setIsLoading(true);
    fetch(`${baseUrl}${CONTACTS}/${username}/${id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("Request failed with status " + response.status);
        }
      })
      .then(() => {
        message.success("Contact deleted successfully");
        setDeleteContact("");
        onContactListGet();
      })
      .catch((error) => {
        console.error("Error:", error);
        setDeleteContact("");
        message.error("Contact can't be deleted");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const editContact = (record: ContactListType) => {
    setIsEdit(true);
    setData(record);
    setIsOpen(true);
  };

  const columns = [
    {
      title: "First Name",
      dataIndex: "firstname",
      key: "firstname",
    },
    {
      title: "Last Name",
      dataIndex: "lastname",
      key: "lastname",
    },
    {
      title: "Number",
      dataIndex: "fax_number",
      key: "fax_number",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
    },
    {
      title: "",
      key: "action",
      fixed: "right" as const,
      render: (_: any, record: any) => (
        <div className="action-wrapper">
          <div className="securefax-action">
            <Button
              type="text"
              className="securefax-action__list"
              onClick={(e) => {
                e.stopPropagation();
                editContact(record);
              }}
              icon={
                <Tooltip title="Edit Contact">
                  <span>
                    <EditDropIcon />
                  </span>
                </Tooltip>
              }
            />
            {record?.contact_source !== "external" && (
              <Button
                type="text"
                className="securefax-action__list"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteContact(record?.contact_id);
                }}
                icon={
                  <Tooltip title="Delete Contact">
                    <span>
                      <DeleteThinIcon width="14" height="14" strokeWidth="2" />
                    </span>
                  </Tooltip>
                }
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <FaxPageStyle>
      <FaxPageHeader>
        <Typography.Title level={5}>{"Contacts"}</Typography.Title>
        <HeaderActions>
          <div className="searchbar-wrapper">
            <Search
              prefix={<SearchIcon />}
              size="small"
              placeholder="Search by firstname or lastname"
              inputMode="search"
              search={contactfilter?.name_query}
              value={contactfilter?.name_query || ""}
              onChange={(e: any) => {
                setContactFilter({
                  ...contactfilter,
                  name_query: e.target.value,
                });
              }}
              maxLength={50}
            />
          </div>
          <div className="mobile-hide">
            <Button
              size="small"
              type="primary"
              onClick={() => setIsOpen(true)}
              icon={<AddIconCollapseIcon width="20" height="20" />}
            >
              Add Contact
            </Button>
          </div>
          <div className="desktop-hide">
            <FloatButton
              icon={<SinglePlusIcon />}
              onClick={() => setIsOpen(true)}
            />
          </div>
        </HeaderActions>
      </FaxPageHeader>
      <div className="mobile-table-block desktop-hide">
        <ContactsTableCard
          contactCardData={ContactList?.contacts ?? []}
          isLoading={isLoading}
          editContact={editContact}
          deleteContact={setDeleteContact}
        />
        {!isLoading && (
          <div className="mobile-pagination">
            <Pagination
              size="small"
              total={ContactList?.total_contacts ?? 0}
              showSizeChanger={true}
              pageSize={contactfilter?.per_page ?? 10}
              current={contactfilter?.page ?? 1}
              onChange={(page, pageSize) => {
                setContactFilter({
                  ...contactfilter,
                  page: page,
                  per_page: pageSize,
                });
              }}
              onShowSizeChange={(page, pageSize) => {
                setContactFilter({
                  ...contactfilter,
                  page: page,
                  per_page: pageSize,
                });
              }}
              showTotal={(total, range) => `${range[1]}/ ${total}`}
            />
          </div>
        )}
      </div>
      <div className="mobile-table-block mobile-hide w-100">
        <Table
          data={
            ContactList?.contacts?.map((contact: ContactListType) => {
              return { ...contact, key: contact.contact_id };
            }) ?? []
          }
          columns={columns}
          isLoading={isLoading}
          pagination={true}
          pageSize={contactfilter?.per_page ?? 10}
          total={ContactList?.total_contacts ?? 0}
          currentPage={contactfilter?.page ?? 1}
          handlePageChange={(pagination: any) => {
            setContactFilter({
              ...contactfilter,
              page: pagination.current,
              per_page: pagination.pageSize,
            });
          }}
          onRowClick={(record: ContactListType) => {
            editContact(record);
          }}
          rowClassName="clickable-rows"
        />
      </div>
      <ConfirmationModal
        open={!!deleteContact}
        title="Are you sure you want to delete this contact?"
        isLoading={isLoading}
        onCancel={() => {
          setDeleteContact(null);
        }}
        onConfirm={() => onDeleteContact(deleteContact)}
      />
      <EditContactModal
        isOpen={isOpen}
        data={data}
        isEdit={isEdit}
        closeModal={() => {
          setIsEdit(false);
          setIsOpen(false);
          setData(null);
        }}
        onYes={async () => {
          await onContactListGet();
          setIsEdit(false);
          setIsOpen(false);
          setData(null);
        }}
      />
    </FaxPageStyle>
  );
};

export default Contacts;
